import React, { FC, useCallback, useState } from "react";
import { useParams } from "@Libs/Router/hooks/useParams";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { ServiceAssignmentEventStep } from "@modules/ServiceAssignment/types/serviceAssignment.types";
import { useRecordEvent } from "@Libs/Events/hooks/useRecordEvent";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";

import { IAppRoute } from "@App/types";
import {
  Subscribe,
  SubscribeSuccessPayload,
  ISubscribeFlowRoute,
} from "@modules/Subscriptions/Flows/Subscribe/Subscribe";
import { acceptServiceOffer } from "@modules/ServiceAssignment/services/serviceOffers";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { useServiceOffer } from "@modules/ServiceAssignment/hooks/useServiceOffer";
import { ConfirmationModal } from "@modules/Subscriptions/Components/ConfirmationModal/ConfirmationModal";

export const BASE_SUBSCRIPTION_FLOW_ROUTE: IAppRoute[] = [
  "SERVICE_OFFERS",
  "SERVICE_OFFER",
  "ACCEPTANCE",
];

export const ServiceOfferAcceptanceSubscriptionFlow: FC = () => {
  const { serviceOfferId } = useParams(BASE_SUBSCRIPTION_FLOW_ROUTE);
  const { currentUser, isLoading: isCurrentUserLoading } = useCurrentUser();
  const navigate = useNavigate();
  const { serviceOffer } = useServiceOffer(serviceOfferId);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [subscribeSuccessPayload, setSubscribeSuccessPayload] =
    useState<SubscribeSuccessPayload>(null);

  const { recordEvent: recordSubscriptionPageViewedEvent } = useRecordEvent(
    {
      key: "subscription_page.viewed",
    },
    { triggerOnMount: false }
  );

  const { recordEvent: recordStepVisitedEvent } = useRecordEvent(
    {
      key: "contract_acceptance_flow.step_visited",
    },
    { triggerOnMount: false }
  );

  const handleSubscribeSuccess = async (
    subscribeSuccessPayload: SubscribeSuccessPayload
  ) => {
    try {
      await acceptServiceOffer(serviceOfferId);
      setSubscribeSuccessPayload(subscribeSuccessPayload);
      setIsSuccessModalOpen(true);
    } catch (e) {
      if (serviceOffer.state === "accepted") {
        setSubscribeSuccessPayload(subscribeSuccessPayload);
        setIsSuccessModalOpen(true);
      }
    }
  };

  const handleConfirm = async () => {
    navigate("LEGACY_SERVICE_OFFER_FLOWS_CONFIRMATION_PAGE", {
      reload: true,
      queryParams: {
        conversation_id: String(serviceOffer?.conversationId),
      },
    });
  };

  const handlePageMount = useCallback(
    (route: ISubscribeFlowRoute) => {
      recordStepVisitedEvent({
        data: {
          contractId: Number(serviceOfferId),
          step: ServiceAssignmentEventStep[route],
        },
      });

      if (!currentUser) {
        return;
      }

      if (route === "CHOOSE_PLAN") {
        recordSubscriptionPageViewedEvent({
          data: {
            userId: currentUser.id,
            contractId: Number(serviceOfferId),
          },
        });
      }
    },
    [currentUser]
  );

  if (isCurrentUserLoading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <Subscribe
        onPageMount={handlePageMount}
        onSuccess={handleSubscribeSuccess}
        onError={() => undefined}
        rootRoute={{
          to: [...BASE_SUBSCRIPTION_FLOW_ROUTE, "SUBSCRIBE"],
          params: { serviceOfferId },
        }}
        backRoute={{
          to: BASE_SUBSCRIPTION_FLOW_ROUTE,
          params: { serviceOfferId },
        }}
        state={{
          relatedServiceId: Number(serviceOfferId),
        }}
      />
      <ConfirmationModal
        isOpen={isSuccessModalOpen}
        onConfirm={handleConfirm}
        paymentMethod={subscribeSuccessPayload?.paymentMethod}
        isPaymentConfirmed={subscribeSuccessPayload?.isPaymentConfirmed}
      />
    </>
  );
};
